import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";
import "./contrats.scss";

import CustomActionMenu from "@components/CustomActionMenu/CustomActionMenu";
import CustomHeader from "@components/CustomHeader/CustomHeader";
import CustomTable from "@components/CustomTable/CustomTable";
import Loader from "@components/Loader/Loader";
import { CreateModalContext } from "layouts/appLayout/layout";
import { useSelector } from "react-redux";

import { useNavigate } from "react-router-dom";
import { useGetContratQuery } from "store/contrat/contrat.api";
import { BASE_URL } from "config/config";
import ContractModal from "./ContractModal/ContractModal";

const ContratsListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);
  const companyId = currentUser?.companyId?._id;

  const [openMenuId, setOpenMenuId] = useState(null);
  const [searchText, setSearchText] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const { data: contrat, isLoading: isLoadingContrats } =
    useGetContratQuery(companyId);

  const { toggleCreateModal } = useContext(CreateModalContext);

  const headers = [
    { key: "document", label: t("contrats.form.document") },
    { key: "Nb recipients", label: t("contrats.form.nb") },
    { key: "status", label: t("contrats.form.status") },
    { key: "createdAt", label: t("contrats.form.createdAt") },
    { key: "certification", label: t("contrats.form.certification") },
  ];

  function getImageURL(docId) {
    return `${BASE_URL}documentsFile/${docId}/file`;
  }

  const renderCell = (key, contrat) => {
    if (key === "document") {
      return (
        <a
          href={getImageURL(contrat?.documentId?._id)}
          target="_blank"
          rel="noopener noreferrer">
          {contrat?.documentId?.title}
        </a>
      );
    }
    if (key === "Nb recipients") {
      return contrat?.recipients?.length || 0;
    }
    if (key === "certification") {
      return contrat?.certificatePath ? (
        <a
          href={getImageURL(contrat?.certificatePath?._id)}
          target="_blank"
          rel="noopener noreferrer">
          {contrat?.certificatePath?.title}
        </a>
      ) : (
        <></>
      );
    }
    if (key === "createdAt")
      return new Date(contrat.createdAt).toLocaleDateString();
    return contrat[key];
  };

  const handleConsult = (contrat) => {
    setOpenMenuId(null);
    toggleCreateModal(
      <ContractModal
        onClose={() => toggleCreateModal(<></>)}
        contrat={contrat}
      />,
      t("contrats.form.title_edit")
    );
  };

  const actionMenu = (contrat) => {
    const actions = [
      {
        label: t("contrats.actions.edit"),
        onClick: () => handleConsult(contrat),
      },
    ];
    return (
      <CustomActionMenu
        openMenuId={openMenuId}
        setOpenMenuId={setOpenMenuId}
        data={contrat}
        actions={actions}
      />
    );
  };

  const onSearchContrat = (text) => {
    setSearchText(text.toLowerCase());
  };

  const filteredContrats = useMemo(() => {
    if (!contrat?.data) return [];
    if (searchText.length <= 1) return contrat.data;

    return contrat.data.filter((c) => {
      return (
        c.documentId?.title?.toLowerCase().includes(searchText) ||
        c.status?.toLowerCase().includes(searchText) ||
        c.recipients?.some((r) => r.email?.toLowerCase().includes(searchText))
      );
    });
  }, [contrat?.data, searchText]);

  // ✅ Pagination
  const pageCount = Math.ceil(filteredContrats.length / itemsPerPage);
  const currentItems = filteredContrats.slice(
    currentPage * itemsPerPage,
    currentPage * itemsPerPage + itemsPerPage
  );

  const handlePageClick = ({ selected }) => setCurrentPage(selected);

  if (isLoadingContrats) return <Loader />;

  return (
    <div className="p-5" style={{ overflowX: "auto" }}>
      <CustomHeader
        title={t("contrats.header_title")}
        dataLength={filteredContrats.length}
        onSearch={onSearchContrat}
      />
      <CustomTable
        headers={headers}
        data={currentItems}
        rowKey="_id"
        renderCell={renderCell}
        actionMenu={actionMenu}
      />

      {/* Pagination */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
        <ReactPaginate
          previousLabel={"←"}
          nextLabel={"→"}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={1}
          pageRangeDisplayed={2}
          onPageChange={handlePageClick}
          containerClassName={"pagination"}
          activeClassName={"active"}
          forcePage={currentPage}
        />
      </div>
    </div>
  );
};

export default ContratsListPage;
